// src/pages/Incidentes.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Pencil,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  X,
  PauseCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import MainLayout from "../components/mainLayout";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/modal";
import "../sass/pages/incidentes.scss";
import ConfirmDialog from "../components/ui/confirmModal";

/* ===================== Config da API exclusiva dos incidentes ===================== */
const API_BASE = "https://apiincidentesgit3-production.up.railway.app";

// Axios para requests “curtos”. SSE não usa axios.
const http = axios.create({
  baseURL: API_BASE,
  timeout: 40000,
});

const ENDPOINT = "/incidentes";

/* ===================== UI helpers ===================== */
const STATUS = {
  0: { label: "Aberto", cls: "badge badge--open", Icon: AlertTriangle },
  1: { label: "Em Atendimento", cls: "badge badge--wip", Icon: Clock },
  2: { label: "Finalizado", cls: "badge badge--done", Icon: CheckCircle2 },
};

function StatusBadge({ value }) {
  const s = STATUS[value] ?? STATUS[0];
  const Icon = s.Icon;
  return (
    <span className={s.cls}>
      <Icon size={14} /> {s.label}
    </span>
  );
}

const normalizeDateRange = (ini, fim) => ({
  dt_ini: ini ? `${ini}T00:00:00` : undefined,
  dt_fim: fim ? `${fim}T23:59:59` : undefined,
});

const fmt = (n) =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString("pt-BR")
    : "-";

const msToHMS = (ms) => {
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (x) => String(x).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

function ProgressBar({ percent }) {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <div
      style={{
        width: "100%",
        height: 10,
        background: "var(--border-muted, #e5e7eb)",
        borderRadius: 999,
        overflow: "hidden",
      }}
      aria-label="Progresso"
    >
      <div
        style={{
          width: `${p}%`,
          height: "100%",
          background:
            "linear-gradient(90deg, #22c55e 0%, #16a34a 50%, #15803d 100%)",
          transition: "width 300ms ease",
        }}
      />
    </div>
  );
}

/* ===================== Página ===================== */
export default function IncidentesScreen() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [status, setStatus] = useState(0);
  const [licenca, setLicenca] = useState("");
  const [tokenVenda, setTokenVenda] = useState("");
  const [dtIni, setDtIni] = useState("");
  const [dtFim, setDtFim] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("view"); // view | edit
  const [item, setItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false, row: null });

  const [resumo, setResumo] = useState({
    abertos: 0,
    emAtendimento: 0,
    finalizados: 0,
    total: 0,
  });

  // ====== seleção em lote (IDs) ======
  const [selected, setSelected] = useState(new Set());

  const toggleOne = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleAllPage = () =>
    setSelected((s) => {
      const n = new Set(s);
      const idsPage = rows.map((r) => r.Id);
      const allOnPage = idsPage.length > 0 && idsPage.every((id) => n.has(id));
      idsPage.forEach((id) => (allOnPage ? n.delete(id) : n.add(id)));
      return n;
    });

  const finalizarSelecionados = async () => {
    if (selected.size === 0) return;
    try {
      await http.patch(
        `${ENDPOINT}/batch/finalizar`,
        {
          ids: [...selected],
          ResponsavelId: 1,
          Observacao: "Finalizado em lote via UI",
        },
        { timeout: 120000 } // um pouco maior
      );
      setSelected(new Set());
      await fetchData();
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detalhes ||
        e?.response?.data?.erro ||
        e.message ||
        "Falha ao finalizar selecionados";
      window.alert(msg);
    }
  };

  // ====== finalizar por descrição (SSE) ======
  const [descDlg, setDescDlg] = useState({
    open: false,
    descricao: "",
    match: "like", // 'exact' | 'like'
    // Campos próprios quando LIKE
    licencaLike: "",
    dtIniLike: "",
    dtFimLike: "",
    batchSize: 500,
    // Progresso / logs
    streaming: false,
    percent: 0,
    total: null,
    totalUpdated: 0,
    batches: 0,
    updatedBatch: 0,
    startedAt: null,
    logs: [],
    error: null,
  });

  const eventSourceRef = useRef(null);
  const logsEndRef = useRef(null);

  const pushLog = (line) =>
    setDescDlg((d) => ({
      ...d,
      logs: [...d.logs, `[${new Date().toLocaleTimeString()}] ${line}`],
    }));

  useEffect(() => {
    // autoscroll para o fim do log
    if (!logsEndRef.current) return;
    logsEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [descDlg.logs]);

  const abrirFinalizarPorDescricao = () =>
    setDescDlg((d) => ({
      ...d,
      open: true,
      error: null,
      // pré-preenche com os filtros atuais
      licencaLike: licenca || "",
      dtIniLike: dtIni || "",
      dtFimLike: dtFim || "",
    }));

  const fecharFinalizarPorDescricao = () => {
    // encerra stream se ainda estiver em aberto
    try {
      eventSourceRef.current?.close?.();
    } catch {}
    eventSourceRef.current = null;

    setDescDlg({
      open: false,
      descricao: "",
      match: "like",
      licencaLike: "",
      dtIniLike: "",
      dtFimLike: "",
      batchSize: 500,
      streaming: false,
      percent: 0,
      total: null,
      totalUpdated: 0,
      batches: 0,
      updatedBatch: 0,
      startedAt: null,
      logs: [],
      error: null,
    });
  };

  // Regra do service: LIKE precisa de Licença ou De/Até
  const likeSemEscopo = useMemo(() => {
    if (descDlg.match !== "like") return false;
    return !descDlg.licencaLike && !descDlg.dtIniLike && !descDlg.dtFimLike;
  }, [descDlg.match, descDlg.licencaLike, descDlg.dtIniLike, descDlg.dtFimLike]);

  const buildStreamUrl = () => {
    const qs = new URLSearchParams();
    qs.set("descricao", descDlg.descricao.trim());
    qs.set("match", descDlg.match);

    // Escopo (se LIKE, usa campos do modal; se EXACT, herdamos os de cima)
    if (descDlg.match === "like") {
      if (descDlg.licencaLike) qs.set("licenca", String(descDlg.licencaLike));
      if (descDlg.dtIniLike) qs.set("dt_ini", `${descDlg.dtIniLike}T00:00:00`);
      if (descDlg.dtFimLike) qs.set("dt_fim", `${descDlg.dtFimLike}T23:59:59`);
    } else {
      if (licenca) qs.set("licenca", String(licenca));
      if (dtIni) qs.set("dt_ini", `${dtIni}T00:00:00`);
      if (dtFim) qs.set("dt_fim", `${dtFim}T23:59:59`);
    }

    // lotes e escopo de status (opcional)
    if (descDlg.batchSize) qs.set("batchSize", String(descDlg.batchSize));
    // limitar a “não finalizados”
    qs.set("statusScope", "0,1");

    return `${API_BASE}${ENDPOINT}/batch/finalizar-por-descricao/stream?${qs.toString()}`;
  };

  const cancelarStream = () => {
    try {
      eventSourceRef.current?.close?.();
    } catch {}
    eventSourceRef.current = null;
    pushLog("Operação cancelada pelo usuário.");
    setDescDlg((d) => ({ ...d, streaming: false }));
  };

  const finalizarPorDescricaoSSE = async () => {
    if (!descDlg.descricao?.trim()) return;
    if (descDlg.match === "like" && likeSemEscopo) {
      window.alert(
        'Para "Contém (LIKE)", informe Licença e/ou um intervalo de datas (De/Até).'
      );
      return;
    }

    // inicia stream
    try {
      setDescDlg((d) => ({
        ...d,
        streaming: true,
        error: null,
        percent: 0,
        total: null,
        totalUpdated: 0,
        batches: 0,
        updatedBatch: 0,
        logs: [],
        startedAt: Date.now(),
      }));

      const url = buildStreamUrl();
      const es = new EventSource(url, { withCredentials: false });
      eventSourceRef.current = es;

      es.addEventListener("start", (ev) => {
        const data = JSON.parse(ev.data || "{}");
        pushLog(`Iniciando… alvo estimado: ${fmt(data.total)} registro(s).`);
        setDescDlg((d) => ({ ...d, total: Number(data.total || 0) }));
      });

      es.addEventListener("progress", (ev) => {
        const data = JSON.parse(ev.data || "{}");
        const {
          updatedBatch = 0,
          totalUpdated = 0,
          batches = 0,
          batchSize = 0,
          percent = 0,
        } = data;
        pushLog(
          `Lote ${fmt(batches)}: +${fmt(updatedBatch)} (total ${fmt(
            totalUpdated
          )})`
        );
        setDescDlg((d) => ({
          ...d,
          updatedBatch,
          totalUpdated,
          batches,
          batchSize,
          percent,
        }));
      });

      es.addEventListener("done", () => {
        pushLog("Concluído ✅");
        setDescDlg((d) => ({ ...d, streaming: false, percent: 100 }));
        es.close();
        eventSourceRef.current = null;
        // atualiza grid
        fetchData();
      });

      es.addEventListener("error", (ev) => {
        let payload = {};
        try {
          payload = JSON.parse(ev?.data || "{}");
        } catch {}
        const msg = payload?.message || "Falha no processamento (SSE).";
        pushLog(`Erro: ${msg}`);
        setDescDlg((d) => ({ ...d, streaming: false, error: msg }));
        es.close();
        eventSourceRef.current = null;
      });
    } catch (e) {
      const msg = e?.message || "Não foi possível iniciar o stream.";
      pushLog(`Erro inicial: ${msg}`);
      setDescDlg((d) => ({ ...d, streaming: false, error: msg }));
    }
  };

  // busca (debounced)
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchDraft);
      setPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [searchDraft]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        status,
        licenca: licenca || undefined,
        token_venda: tokenVenda || undefined,
        search: search || undefined,
        view: "slim", // colunas leves
        ...normalizeDateRange(dtIni, dtFim),
      };

      const paramsResumo = { ...params };
      delete paramsResumo.status;
      delete paramsResumo.view;

      const [listRes, resumoRes] = await Promise.all([
        http.get(ENDPOINT, { params, timeout: 60000 }),
        http.get(`${ENDPOINT}/resumo`, {
          params: paramsResumo,
          timeout: 45000,
        }),
      ]);

      const list = listRes?.data || {};
      setRows(Array.isArray(list) ? list : list.data || []);
      setHasNext(Boolean(list.hasNext));

      const rsum =
        resumoRes?.data ?? {
          abertos: 0,
          emAtendimento: 0,
          finalizados: 0,
          total: 0,
        };
      setResumo(rsum);

      const totalView =
        status === 0 ? rsum.abertos : status === 1 ? rsum.emAtendimento : rsum.finalizados;

      setTotal(Number.isFinite(totalView) ? totalView : 0);
    } catch (e) {
      console.error("✖ Erro no fetchData:", e?.response?.data || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(); // eslint-disable-next-line
  }, [page, limit, status, licenca, search, dtIni, dtFim, tokenVenda]);

  // ====== carregar detalhes completos ao abrir modal ======
  const loadDetails = async (id) => {
    setDetailLoading(true);
    try {
      const res = await http.get(`${ENDPOINT}/${id}`, { timeout: 30000 });
      const full = res?.data || {};
      setItem((prev) => ({ ...(prev || {}), ...full }));
    } catch (e) {
      console.error("Falha ao carregar detalhes:", e?.response?.data || e);
      const msg =
        e?.response?.data?.detalhes ||
        e?.response?.data?.erro ||
        e.message ||
        "Não foi possível carregar os detalhes.";
      window.alert(msg);
    } finally {
      setDetailLoading(false);
    }
  };

  const openView = (r) => {
    setItem(r);
    setMode("view");
    setOpen(true);
    loadDetails(r.Id);
  };
  const openEdit = (r) => {
    setItem({ ...r });
    setMode("edit");
    setOpen(true);
    loadDetails(r.Id);
  };
  const closeModal = () => {
    setOpen(false);
    setItem(null);
    setMode("view");
    setDetailLoading(false);
  };

  const onSave = async () => {
    if (!item?.Id) return;
    setSaving(true);
    try {
      const payload = {
        NomePDV: item.NomePDV ?? null,
        DescricaoErro: item.DescricaoErro ?? null,
        Linha: item.Linha ?? null,
        StackTrace: item.StackTrace ?? null,
        Token_Venda: item.Token_Venda ?? null,
        info_adicional: item.info_adicional ?? null,
      };
      await http.put(`${ENDPOINT}/${item.Id}`, payload, { timeout: 45000 });
      await fetchData();
      closeModal();
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detalhes ||
        e?.response?.data?.erro ||
        e.message ||
        "Falha ao salvar alterações";
      window.alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const onChangeStatus = async (r, newStatus) => {
    try {
      await http.patch(
        `${ENDPOINT}/${r.Id}/status`,
        {
          Status: newStatus,
          ResponsavelId: 1,
          Observacao:
            newStatus === 2 ? "Finalizado via UI" : "Atualização de status via UI",
        },
        { timeout: 30000 }
      );
      await fetchData();
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detalhes ||
        e?.response?.data?.erro ||
        e.message ||
        "Falha ao atualizar status";
      window.alert(msg);
    }
  };

  const onDeleteClick = (row) => setConfirmState({ open: true, row });
  const handleConfirmDelete = async () => {
    if (!confirmState.row) return;
    try {
      await http.delete(`${ENDPOINT}/${confirmState.row.Id}`, {
        timeout: 30000,
      });
      await fetchData();
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detalhes ||
        e?.response?.data?.erro ||
        e.message ||
        "Falha ao excluir incidente";
      window.alert(msg);
    } finally {
      setConfirmState({ open: false, row: null });
    }
  };

  const columns = useMemo(
    () => [
      { key: "Id", label: "ID", width: 80 },
      { key: "DataHoraErro", label: "Data/Hora", width: 180 },
      { key: "NrLicenca", label: "Licença", width: 100 },
      { key: "NomePDV", label: "PDV", width: 160 },
      { key: "Token_Venda", label: "Token Venda", width: 220 },
      { key: "DescricaoErro", label: "Descrição", width: "auto" },
      { key: "Status", label: "Status", width: 160 },
      { key: "_actions", label: "Ações", width: 260 },
    ],
    []
  );

  const pages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const elapsed = descDlg.startedAt ? Date.now() - descDlg.startedAt : 0;

  return (
    <MainLayout>
      <main className="incidentes">
        <header className="incidentes__header">
          <h1>Gestão de Erros</h1>
          <div className="subtitle">Monitore erros e avance o workflow</div>
        </header>

        <section className="incidentes__filters card">
          <div className="filters-grid">
            <div className="md:col-span-2">
              <Label className="form-label">Buscar (descrição/stack/token/info)</Label>
              <Input
                className="form-control"
                placeholder="Ex.: IndexOutOfRange"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
            </div>

            <div className="span-3">
              <Label className="form-label">Status</Label>
              <select
                className="form-control"
                value={String(status)}
                onChange={(e) => {
                  setStatus(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value="0">Aberto</option>
                <option value="1">Em Atendimento</option>
                <option value="2">Finalizado</option>
              </select>
            </div>

            <div className="span-3">
              <Label className="form-label">Licença</Label>
              <Input
                className="form-control"
                type="number"
                placeholder="NrLicenca"
                value={licenca}
                onChange={(e) => {
                  setLicenca(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="span-3">
              <Label className="form-label">Token da Venda</Label>
              <Input
                className="form-control"
                placeholder="Token_Venda"
                value={tokenVenda}
                onChange={(e) => {
                  setTokenVenda(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="span-3">
              <Label className="form-label">De</Label>
              <Input
                className="form-control"
                type="date"
                value={dtIni}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => {
                  setDtIni(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="span-3">
              <Label className="form-label">Até</Label>
              <Input
                className="form-control"
                type="date"
                value={dtFim}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => {
                  setDtFim(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="actions-bar" style={{ gap: 8 }}>
              <Button
                variant="secondary"
                onClick={() => {
                  setPage(1);
                  setStatus(0);
                  setLicenca("");
                  setSearch("");
                  setSearchDraft("");
                  setDtIni("");
                  setDtFim("");
                  setTokenVenda("");
                  setSelected(new Set());
                }}
              >
                Limpar
              </Button>
              <Button onClick={fetchData}>Atualizar</Button>
              <Button variant="outline" onClick={abrirFinalizarPorDescricao}>
                Finalizar por descrição…
              </Button>
            </div>
          </div>
        </section>

        {/* Barra de ações em lote (IDs selecionados) */}
        {selected.size > 0 && (
          <section
            className="card"
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <strong>{selected.size}</strong> selecionado(s)
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={finalizarSelecionados}>
                <CheckCircle2 size={16} /> Finalizar selecionados
              </Button>
              <Button variant="ghost" onClick={() => setSelected(new Set())}>
                Limpar seleção
              </Button>
            </div>
          </section>
        )}

        <section className="card" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span className="badge badge--open">Abertos: {fmt(resumo.abertos)}</span>
          <span className="badge badge--wip">Em Atendimento: {fmt(resumo.emAtendimento)}</span>
          <span className="badge badge--done">Finalizados: {fmt(resumo.finalizados)}</span>
        </section>

        <section className="incidentes__grid card">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {/* seleção da página */}
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && rows.every((r) => selected.has(r.Id))}
                      onChange={toggleAllPage}
                    />
                  </th>

                  {columns.map((c) => (
                    <th key={c.key} style={{ width: c.width }}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="tbl__loading">
                      <Loader2 className="spin" /> Carregando...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="tbl__empty">
                      Nenhum incidente encontrado.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.Id}>
                      {/* checkbox linha */}
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(r.Id)}
                          onChange={() => toggleOne(r.Id)}
                        />
                      </td>

                      <td>{r.Id}</td>
                      <td>{new Date(r.DataHoraErro).toLocaleString("pt-BR")}</td>
                      <td>{r.NrLicenca}</td>
                      <td>{r.NomePDV || "-"}</td>
                      <td className="truncate" title={r.Token_Venda || ""}>
                        {r.Token_Venda || "-"}
                      </td>
                      <td className="truncate" title={r.DescricaoErro}>
                        {r.DescricaoErro}
                      </td>
                      <td>
                        <StatusBadge value={r.Status} />
                      </td>
                      <td>
                        <div className="row-actions">
                          <Button
                            variant="outline"
                            size="icon"
                            title="Visualizar"
                            onClick={() => openView(r)}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            title="Editar"
                            onClick={() => openEdit(r)}
                          >
                            <Pencil size={16} />
                          </Button>
                          {r.Status !== 1 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onChangeStatus(r, 1)}
                            >
                              <Clock size={14} /> Em Atend.
                            </Button>
                          )}
                          {r.Status !== 2 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onChangeStatus(r, 2)}
                            >
                              <CheckCircle2 size={14} /> Finalizar
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="icon"
                            title="Excluir"
                            onClick={() => onDeleteClick(r)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="paginator">
            <div>
              Exibindo {fmt(from)}–{fmt(to)} de {fmt(total)}
            </div>
            <div className="nav">
              <Button
                variant="outline"
                size="icon"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft size={16} />
              </Button>
              <span>
                Página {page} / {Math.max(1, Math.ceil(total / limit))}
              </span>
              <Button
                variant="outline"
                size="icon"
                disabled={page >= pages || (!hasNext && page * limit >= total)}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </section>

        {/* Modal de detalhes/edição */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent maxWidth="880px">
            <DialogHeader>
              <DialogTitle>
                {mode === "view" ? "Detalhes do Incidente" : "Editar Incidente"}
                {detailLoading && (
                  <span style={{ marginLeft: 12, fontSize: 12, opacity: 0.7 }}>
                    <Loader2 className="spin" style={{ display: "inline", verticalAlign: "middle" }} />{" "}
                    carregando dados completos…
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            {item && (
              <div className="modal-form">
                <div className="col-4">
                  <Label className="form-label">ID</Label>
                  <Input value={item.Id} readOnly />
                </div>
                <div className="col-4">
                  <Label className="form-label">Data/Hora</Label>
                  <Input
                    value={new Date(item.DataHoraErro).toLocaleString("pt-BR")}
                    readOnly
                  />
                </div>
                <div className="col-4">
                  <Label className="form-label">Status</Label>
                  <Input value={STATUS[item.Status]?.label ?? "-"} readOnly />
                </div>

                <div className="col-4">
                  <Label className="form-label">Licença</Label>
                  <Input value={item.NrLicenca ?? ""} readOnly />
                </div>
                <div className="col-4">
                  <Label className="form-label">PDV</Label>
                  <Input
                    value={item.NomePDV ?? ""}
                    readOnly={mode === "view"}
                    onChange={(e) => setItem({ ...item, NomePDV: e.target.value })}
                  />
                </div>
                <div className="col-4">
                  <Label className="form-label">Linha</Label>
                  <Input
                    type="number"
                    value={item.Linha ?? ""}
                    readOnly={mode === "view"}
                    onChange={(e) =>
                      setItem({
                        ...item,
                        Linha: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="col-12">
                  <Label className="form-label">Descrição do Erro</Label>
                  <textarea
                    className="form-control"
                    value={item.DescricaoErro ?? ""}
                    readOnly={mode === "view"}
                    onChange={(e) => setItem({ ...item, DescricaoErro: e.target.value })}
                  />
                </div>

                <div className="col-12">
                  <Label className="form-label">StackTrace</Label>
                  <textarea
                    className="form-control"
                    value={item.StackTrace ?? ""}
                    readOnly={mode === "view"}
                    onChange={(e) => setItem({ ...item, StackTrace: e.target.value })}
                  />
                </div>

                <div className="col-12">
                  <Label className="form-label">Token_Venda</Label>
                  <Input
                    value={item.Token_Venda ?? ""}
                    readOnly={mode === "view"}
                    onChange={(e) => setItem({ ...item, Token_Venda: e.target.value })}
                  />
                </div>

                <div className="col-12">
                  <Label className="form-label">info_adicional (texto/JSON)</Label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={item.info_adicional ?? ""}
                    readOnly={mode === "view"}
                    onChange={(e) => setItem({ ...item, info_adicional: e.target.value })}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              {mode === "edit" ? (
                <Button onClick={onSave} disabled={saving || detailLoading}>
                  {saving ? (
                    <>
                      <Loader2 className="spin mr-2" /> Salvando...
                    </>
                  ) : (
                    "Salvar alterações"
                  )}
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setMode("edit")}>
                  Editar
                </Button>
              )}
              <Button variant="secondary" onClick={closeModal}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Finalizar por Descrição (SSE) */}
        <Dialog
          open={descDlg.open}
          onOpenChange={(o) => {
            if (!o) fecharFinalizarPorDescricao();
            else setDescDlg((d) => ({ ...d, open: o }));
          }}
        >
          <DialogContent maxWidth="720px">
            <DialogHeader>
              <DialogTitle>Finalizar por descrição</DialogTitle>
            </DialogHeader>

            <div className="modal-form">
              <div className="col-12">
                <Label className="form-label">Descrição</Label>
                <Input
                  placeholder="Ex.: System.InvalidOperationException"
                  value={descDlg.descricao}
                  disabled={descDlg.streaming}
                  onChange={(e) =>
                    setDescDlg((d) => ({ ...d, descricao: e.target.value }))
                  }
                />
              </div>

              <div className="col-6">
                <Label className="form-label">Modo de comparação</Label>
                <select
                  className="form-control"
                  value={descDlg.match}
                  disabled={descDlg.streaming}
                  onChange={(e) =>
                    setDescDlg((d) => ({ ...d, match: e.target.value }))
                  }
                >
                  <option value="like">Contém (LIKE)</option>
                  <option value="exact">Igual (EXACT)</option>
                </select>
              </div>

              <div className="col-6">
                <Label className="form-label">Tamanho do lote</Label>
                <Input
                  type="number"
                  min={100}
                  max={1000}
                  step={50}
                  value={descDlg.batchSize}
                  disabled={descDlg.streaming}
                  onChange={(e) =>
                    setDescDlg((d) => ({
                      ...d,
                      batchSize:
                        e.target.value === "" ? 500 : Math.max(100, Math.min(1000, Number(e.target.value))),
                    }))
                  }
                />
              </div>

              {/* Campos próprios quando LIKE */}
              {descDlg.match === "like" && (
                <>
                  <div className="col-4">
                    <Label className="form-label">Nr. Licença (escopo)</Label>
                    <Input
                      placeholder="Opcional"
                      type="number"
                      value={descDlg.licencaLike}
                      disabled={descDlg.streaming}
                      onChange={(e) =>
                        setDescDlg((d) => ({ ...d, licencaLike: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-4">
                    <Label className="form-label">De (escopo)</Label>
                    <Input
                      type="date"
                      value={descDlg.dtIniLike}
                      disabled={descDlg.streaming}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) =>
                        setDescDlg((d) => ({ ...d, dtIniLike: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-4">
                    <Label className="form-label">Até (escopo)</Label>
                    <Input
                      type="date"
                      value={descDlg.dtFimLike}
                      disabled={descDlg.streaming}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) =>
                        setDescDlg((d) => ({ ...d, dtFimLike: e.target.value }))
                      }
                    />
                  </div>

                  <div className="col-12" style={{ color: likeSemEscopo ? "#b42318" : "#6b7280" }}>
                    Para <strong>Contém (LIKE)</strong>, informe ao menos a <strong>Licença</strong> e/ou
                    um intervalo <strong>De/Até</strong>.
                  </div>
                </>
              )}

              {/* Progresso / Logs */}
              {descDlg.streaming && (
                <>
                  <div className="col-12" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <ProgressBar percent={descDlg.percent} />
                    <div style={{ width: 80, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {descDlg.percent ?? 0}%
                    </div>
                  </div>

                  <div className="col-12" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 8 }}>
                    <div className="mini-stat">
                      <div className="mini-stat__label">Total alvo</div>
                      <div className="mini-stat__value">{fmt(descDlg.total)}</div>
                    </div>
                    <div className="mini-stat">
                      <div className="mini-stat__label">Atualizados</div>
                      <div className="mini-stat__value">{fmt(descDlg.totalUpdated)}</div>
                    </div>
                    <div className="mini-stat">
                      <div className="mini-stat__label">Lotes</div>
                      <div className="mini-stat__value">{fmt(descDlg.batches)}</div>
                    </div>
                    <div className="mini-stat">
                      <div className="mini-stat__label">Tempo</div>
                      <div className="mini-stat__value">{msToHMS(elapsed)}</div>
                    </div>
                  </div>

                  <div className="col-12">
                    <Label className="form-label">Logs</Label>
                    <div
                      style={{
                        border: "1px solid var(--border-muted, #e5e7eb)",
                        borderRadius: 8,
                        padding: 8,
                        height: 160,
                        overflow: "auto",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 12,
                        background: "#fafafa",
                      }}
                    >
                      {descDlg.logs.map((l, i) => (
                        <div key={i}>{l}</div>
                      ))}
                      <div ref={logsEndRef} />
                    </div>
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              {!descDlg.streaming ? (
                <>
                  <Button
                    onClick={finalizarPorDescricaoSSE}
                    disabled={
                      !descDlg.descricao.trim() ||
                      (descDlg.match === "like" && likeSemEscopo)
                    }
                    title={
                      descDlg.match === "like" && likeSemEscopo
                        ? 'Para "Contém (LIKE)", informe Licença e/ou De/Até.'
                        : undefined
                    }
                  >
                    <CheckCircle2 size={16} /> Iniciar finalização
                  </Button>
                  <Button variant="secondary" onClick={fecharFinalizarPorDescricao}>
                    Fechar
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="destructive" onClick={cancelarStream}>
                    <PauseCircle size={16} /> Cancelar
                  </Button>
                  <Button variant="ghost" onClick={fecharFinalizarPorDescricao}>
                    <X size={16} /> Fechar
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={confirmState.open}
          titulo="Confirmar exclusão"
          mensagem="Tem certeza que deseja excluir este incidente?"
          onClose={() => setConfirmState({ open: false, row: null })}
          onConfirm={handleConfirmDelete}
        />
      </main>
    </MainLayout>
  );
}
